import { useQueryClient, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useAlerts } from "./weatherQueries";
import { useRoutineDay, useTasks, useTicker, useWeather } from "./queries";
import { useLists } from "../widgets/kitchen/api";
import { useCountdowns, useNotes } from "../widgets/home/api";

// Every screen should open on data that is already fresh. Three layers:
//  1. Saved snapshots (persist.ts) show the last copy the moment the app loads.
//  2. KeepWarm keeps the household's everyday data loading in the background
//     from the start, whichever screen is up, on each hook's own schedule.
//  3. startInactiveRefresh keeps data from screens shown earlier fresh while they
//     are hidden, so the screen schedule never opens on an old copy.

const MIN = 60_000;
/** Inactive data newer than this is left alone even if its hook polls faster. */
const MIN_AGE_MS = 5 * MIN;
const DEFAULT_AGE_MS = 15 * MIN;
// Admin-only, one-off or per-person lookups that aren't worth polling unseen.
const SKIP = new Set(["me", "auth-config", "diagnostics", "ai-status", "calendar-choices", "calendar-status", "ledger", "media", "media-review"]);

/**
 * Whether a query belongs to a screen that's turned off (shared/screens.ts):
 * its first key part is one of the screen's, except the screen's own settings,
 * which the admin page and the screen's switch still read.
 */
const isOff = (key: QueryKey, offKeys: readonly string[]) => offKeys.includes(String(key[0])) && key[1] !== "settings";

/**
 * Mounted once on the display: these hooks feed most screens. `offKeys` are
 * the query keys of screens turned off; their data is dropped and never
 * refreshed in the background.
 */
export function KeepWarm({ offKeys }: { offKeys: readonly string[] }) {
  useWeather(true);
  useAlerts();
  useTasks();
  useRoutineDay();
  useTicker();
  useLists();
  useNotes();
  useCountdowns();
  const qc = useQueryClient();
  const off = useRef(offKeys);
  off.current = offKeys;
  const offKey = offKeys.join(",");
  useEffect(() => {
    if (offKey) qc.removeQueries({ predicate: (q) => isOff(q.queryKey, off.current) });
  }, [qc, offKey]);
  useEffect(() => startInactiveRefresh(qc, () => off.current), [qc]);
  return null;
}

function startInactiveRefresh(qc: QueryClient, offKeys: () => readonly string[]): () => void {
  let running = false;
  let stopped = false;
  const pass = async () => {
    if (running || document.hidden || !navigator.onLine) return;
    running = true;
    try {
      const now = Date.now();
      const due = qc
        .getQueryCache()
        .findAll({ type: "inactive" })
        .filter((q) => {
          if (q.state.status !== "success" || q.state.fetchStatus !== "idle" || !q.options.queryFn) return false;
          if (SKIP.has(String(q.queryKey[0])) || isOff(q.queryKey, offKeys())) return false;
          // A query keeps the options of the hook that last used it, including its polling interval.
          const hookEvery = (q.options as { refetchInterval?: unknown }).refetchInterval;
          const every = typeof hookEvery === "number" ? hookEvery : DEFAULT_AGE_MS;
          return now - q.state.dataUpdatedAt > Math.max(MIN_AGE_MS, every);
        });
      // One at a time with a pause between, so a Pi never gets a burst.
      for (const q of due) {
        if (stopped || document.hidden) break;
        await qc.refetchQueries({ queryKey: q.queryKey, exact: true, type: "inactive" }).catch(() => undefined);
        await new Promise((r) => setTimeout(r, 1500));
      }
    } finally {
      running = false;
    }
  };
  const timer = window.setInterval(pass, 5 * MIN);
  // Waking the screen or coming back online catches up straight away.
  const wake = () => void pass();
  document.addEventListener("visibilitychange", wake);
  window.addEventListener("online", wake);
  return () => {
    stopped = true;
    clearInterval(timer);
    document.removeEventListener("visibilitychange", wake);
    window.removeEventListener("online", wake);
  };
}
